import { useParams, Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { useState } from "react";

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

function formatViews(num: number) {
  if (num >= 10000) return `${(num / 10000).toFixed(1)}万`;
  return num.toString();
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<"latest" | "hot">("latest");

  const category = categories.find((c) => c.slug === slug);

  let categoryArticles = articles.filter((a) => a.categorySlug === slug);
  if (categoryArticles.length === 0) {
    categoryArticles = articles;
  }

  const sortedArticles = [...categoryArticles].sort((a, b) => {
    if (sortBy === "hot") return b.views - a.views;
    return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
  });

  const hotInCategory = sortedArticles.filter((a) => a.isHot).slice(0, 3);

  return (
    <main>
      {/* Schema.org BreadcrumbList */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "http://rbecpr.cn/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: category?.name || "分类",
              item: `http://rbecpr.cn/category/${slug}`,
            },
          ],
        })}
      </script>

      {/* Category Hero */}
      <section className="relative min-h-[400px] md:min-h-[480px] flex flex-col md:flex-row overflow-hidden bg-foreground-950">
        <div className="w-full md:w-1/2 flex items-center px-6 md:px-12 lg:px-16 py-12 md:py-0">
          <div className="max-w-lg">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: category?.name || "分类浏览" },
              ]}
            />
            <h1
              className="text-3xl md:text-4xl lg:text-5xl font-black text-background-50 mt-4 mb-4"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              {category?.name || "全部分类"}
            </h1>
            <p className="text-sm md:text-base text-background-300 leading-relaxed mb-6">
              {category?.description || "浏览爆料网全部爆料内容，实时追踪网红主播塌房现场、明星反差视频与独家吃瓜内容。"}
            </p>
            <div className="flex items-center gap-4 text-sm text-background-400">
              <span className="flex items-center gap-1">
                <i className="ri-article-line"></i>
                {sortedArticles.length} 篇文章
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-fire-line"></i>
                {hotInCategory.length} 条热榜
              </span>
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/2 relative min-h-[200px] md:min-h-auto">
          <img
            src={category?.slug === "influencer"
              ? "https://readdy.ai/api/search-image?query=social%20media%20influencer%20live%20streaming%20setup%20with%20ring%20lights%20and%20phone%20cameras%2C%20dramatic%20red%20and%20black%20lighting%2C%20dark%20moody%20atmosphere%2C%20behind%20the%20scenes%20content%20creation%20studio%2C%20cinematic%20documentary%20style&width=800&height=600&seq=cat-influencer&orientation=landscape"
              : category?.slug === "celebrity"
                ? "https://readdy.ai/api/search-image?query=celebrity%20red%20carpet%20event%20with%20flash%20photography%2C%20paparazzi%20cameras%20and%20spotlights%2C%20dark%20glamorous%20atmosphere%2C%20red%20accent%20lighting%2C%20cinematic%20entertainment%20photography%20style&width=800&height=600&seq=cat-celebrity&orientation=landscape"
                : category?.slug === "exclusive"
                  ? "https://readdy.ai/api/search-image?query=investigative%20journalist%20desk%20with%20documents%20and%20evidence%20photos%2C%20dramatic%20desk%20lamp%20lighting%2C%20dark%20moody%20noir%20atmosphere%2C%20red%20accent%20light%2C%20cinematic%20documentary%20style&width=800&height=600&seq=cat-exclusive&orientation=landscape"
                  : category?.slug === "society"
                    ? "https://readdy.ai/api/search-image?query=urban%20street%20scene%20with%20crowd%20protest%20and%20news%20reporters%2C%20dramatic%20overcast%20sky%2C%20social%20movement%20atmosphere%2C%20documentary%20photography%20style%2C%20high%20contrast%2C%20moody%20cinematic%20lighting&width=800&height=600&seq=cat-society&orientation=landscape"
                    : category?.slug === "finance"
                      ? "https://readdy.ai/api/search-image?query=stock%20market%20trading%20floor%20with%20digital%20screens%20showing%20red%20numbers%2C%20dark%20dramatic%20atmosphere%2C%20financial%20scandal%20aesthetic%2C%20modern%20corporate%20interior%2C%20cinematic%20documentary%20photography&width=800&height=600&seq=cat-finance&orientation=landscape"
                      : "https://readdy.ai/api/search-image?query=breaking%20news%20digital%20collage%20with%20multiple%20screens%20and%20headlines%2C%20dark%20background%20with%20red%20accent%20highlights%2C%20modern%20media%20war%20room%20aesthetic%2C%20cinematic%20photography&width=800&height=600&seq=cat-default&orientation=landscape"}
            alt={category?.name || "分类"}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/60 to-transparent"></div>
        </div>
      </section>

      {/* Hot articles in category */}
      {hotInCategory.length > 0 && (
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
              <i className="ri-fire-fill text-primary-500"></i>
              {category?.name || "热门"}热榜
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {hotInCategory.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group relative overflow-hidden rounded-lg block"
                >
                  <div className="aspect-[16/10] relative">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="flex items-center gap-2 mb-1.5 text-xs text-background-300">
                        <span className="px-1.5 py-0.5 rounded bg-primary-500 text-background-50 font-bold text-[10px]">
                          热
                        </span>
                        <span>{formatDate(article.publishedAt)}</span>
                        <span>·</span>
                        <span>{formatViews(article.views)}浏览</span>
                      </div>
                      <h3 className="text-sm md:text-base font-bold text-background-50 leading-snug group-hover:text-primary-300 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Article list with sort */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950">
              全部文章
            </h2>
            <div className="flex items-center gap-1 bg-background-100 rounded-md p-1">
              <button
                onClick={() => setSortBy("latest")}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                  sortBy === "latest"
                    ? "bg-background-50 text-foreground-950 shadow-sm"
                    : "text-foreground-400 hover:text-foreground-600"
                }`}
              >
                最新
              </button>
              <button
                onClick={() => setSortBy("hot")}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                  sortBy === "hot"
                    ? "bg-background-50 text-foreground-950 shadow-sm"
                    : "text-foreground-400 hover:text-foreground-600"
                }`}
              >
                最热
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {sortedArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group flex gap-4 md:gap-6 bg-background-100 rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="w-28 h-20 md:w-40 md:h-28 rounded-md overflow-hidden flex-shrink-0">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 text-xs text-foreground-400">
                    <span className="text-primary-500 font-medium">
                      {article.category}
                    </span>
                    {article.isExclusive && (
                      <span className="px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 font-bold text-[10px]">
                        独家
                      </span>
                    )}
                    {article.isHot && (
                      <span className="px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 font-bold text-[10px]">
                        热
                      </span>
                    )}
                    <span>{formatDate(article.publishedAt)}</span>
                  </div>
                  <h3 className="text-sm md:text-base font-bold text-foreground-950 group-hover:text-primary-500 transition-colors leading-snug line-clamp-2 mb-1.5">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2 hidden md:block mb-2">
                    {article.summary}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatViews(article.views)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Related categories */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-6">
            浏览其他分类
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {categories
              .filter((c) => c.slug !== slug)
              .map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-background-50 hover:bg-background-200 transition-colors border border-background-200"
                >
                  <div className="w-9 h-9 rounded-md bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${cat.icon} text-primary-500 text-sm`}></i>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground-950 truncate">
                      {cat.name}
                    </div>
                    <div className="text-xs text-foreground-400">
                      {cat.articleCount}篇
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </main>
  );
}
